import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, MapPin, Users, Star, AlertCircle } from 'lucide-react';
import { API_GATEWAY_ABS_URL } from '../../config/env';
import './AuthLoginPage.css';

const AuthLoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Spring Security redirects to {loginPage}?error on a failed login (wrong email/password).
  const [authError] = useState(() => new URLSearchParams(window.location.search).has('error'));

  // POST directly to the API GW — same domain as the session cookie (apigw.staging.getspeakhub.com).
  // This ensures the session (with the saved OAuth2 authorization request) is found after login,
  // so Spring Security redirects correctly to the PKCE callback (getspeakhub://auth/callback).
  const loginAction = `${API_GATEWAY_ABS_URL}/login`;

  return (
    <div className="auth-login-root">
      {/* ── LEFT PANEL ── */}
      <div className="alp-left">
        <div className="alp-grid" />

        <div className="alp-left-top">
          {/* Logo */}
          <a href="/" className="alp-logo">
            <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="GetSpeakHub" className="alp-logo-mark" />
            <span className="alp-logo-text">GetSpeakHub</span>
          </a>

          {/* Headline */}
          <h2 className="alp-headline">
            Welcome<br />back,<br />
            <span className="alp-headline-accent">stranger.</span>
          </h2>
          <p className="alp-sub">
            Your next conversation is waiting. Sign in and find a table near you.
          </p>

          {/* Floating language cards */}
          <div className="alp-cards">
            <div className="alp-card alp-card-1">
              <span className="alp-card-flag">🇬🇧</span>
              <span className="alp-card-lang">English</span>
              <span className="alp-card-level">B2</span>
            </div>
            <div className="alp-card alp-card-2">
              <span className="alp-card-flag">🇯🇵</span>
              <span className="alp-card-lang">Japanese</span>
              <span className="alp-card-level">A1</span>
            </div>
            <div className="alp-card alp-card-3">
              <span className="alp-card-flag">🇩🇪</span>
              <span className="alp-card-lang">German</span>
              <span className="alp-card-level">C1</span>
            </div>
          </div>

          {/* Features */}
          <div className="alp-features">
            <div className="alp-feat">
              <div className="alp-feat-icon">
                <MapPin size={18} />
              </div>
              <div>
                <div className="alp-feat-title">Discover Events</div>
                <div className="alp-feat-desc">
                  Find language meetups on a map or list, filtered by language and level.
                </div>
              </div>
            </div>
            <div className="alp-feat">
              <div className="alp-feat-icon">
                <Users size={18} />
              </div>
              <div>
                <div className="alp-feat-title">Join Real Conversations</div>
                <div className="alp-feat-desc">
                  Practice with native speakers and fellow learners in a welcoming space.
                </div>
              </div>
            </div>
            <div className="alp-feat">
              <div className="alp-feat-icon">
                <Star size={18} />
              </div>
              <div>
                <div className="alp-feat-title">Build Reputation</div>
                <div className="alp-feat-desc">
                  Earn points by hosting and attending. Your score builds community trust.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Left Bottom — testimonial */}
        <div className="alp-left-bottom">
          <div className="alp-testimonial">
            <p className="alp-testimonial-quote">
              "GetSpeakHub helped me go from intermediate to fluent in Spanish within a year.
              The in-person meetups made all the difference."
            </p>
            <div className="alp-testimonial-author">
              <div className="alp-testimonial-avatar">AK</div>
              <div>
                <div className="alp-testimonial-name">Anna K.</div>
                <div className="alp-testimonial-role">Language learner · Istanbul</div>
              </div>
            </div>
          </div>
          <p className="alp-copy">© 2026 GetSpeakHub, Inc.</p>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="alp-right">
        <div className="alp-form-box">
          <div className="alp-form-header">
            <h1 className="alp-form-title">Sign in with email</h1>
            <p className="alp-form-sub">Enter your email and password to continue.</p>
          </div>

          {authError && (
            <div className="alp-auth-error" role="alert">
              <AlertCircle size={18} />
              <span>Invalid email or password. Please try again.</span>
            </div>
          )}

          <form
            method="POST"
            action={loginAction}
            onSubmit={() => setLoading(true)}
          >
            {/* Email / Username */}
            <div className="alp-field">
              <label className="alp-field-label">Email or Username</label>
              <div className="alp-field-wrap">
                <span className="alp-field-icon">
                  <Mail size={18} />
                </span>
                <input
                  type="text"
                  name="username"
                  className="alp-input"
                  placeholder="you@example.com"
                  autoCapitalize="none"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="alp-field">
              <div className="alp-field-row">
                <label className="alp-field-label" style={{ marginBottom: 0 }}>
                  Password
                </label>
                <a href="/forgot-password" className="alp-forgot">
                  Forgot password?
                </a>
              </div>
              <div className="alp-field-wrap" style={{ marginTop: 8 }}>
                <span className="alp-field-icon">
                  <Lock size={18} />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="alp-input alp-input-pass"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="alp-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" className="alp-btn-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="alp-spinner" />
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="alp-form-legal">
            By signing in, you accept GetSpeakHub's{' '}
            <a href="/terms">Terms of Service</a> and{' '}
            <a href="/privacy">Privacy Policy</a>.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLoginPage;
